/**
 * Export Service - 导出服务
 * 提供 Deal 数据导出功能
 */
import api from './api'

// ============================================
// 类型定义
// ============================================

export type ExportFormat = 'json' | 'markdown'
export type ExportType = 'full' | 'summary' | 'hypothesis' | 'evidence'

export interface ExportRequest {
  export_type: ExportType
  format: ExportFormat
}

export interface ExportMeta {
  deal_id: string
  export_type: string
  format: string
  exported_at: string
  is_masked: boolean
  disclaimer: string
}

export interface ExportData {
  export_meta: ExportMeta
  deal?: Record<string, any>
  hypotheses?: Record<string, any>[]
  evidences?: Record<string, any>[]
  persons?: Record<string, any>[]
  summary?: Record<string, any>
}

export interface ExportResponse {
  success: boolean
  data?: ExportData
  markdown?: string
  message?: string
}

// ============================================
// 导出类型显示名称
// ============================================

export const exportTypeLabels: Record<ExportType, string> = {
  full: '完整导出',
  summary: '摘要导出',
  hypothesis: '仅假设',
  evidence: '仅证据'
}

export const exportTypeDescriptions: Record<ExportType, string> = {
  full: '包含所有假设、证据和联系人（脱敏）',
  summary: '包含项目概况和关键统计',
  hypothesis: '仅包含假设列表（脱敏）',
  evidence: '仅包含证据摘要（脱敏）'
}

export const exportFormatLabels: Record<ExportFormat, string> = {
  json: 'JSON',
  markdown: 'Markdown'
}

// ============================================
// API 调用
// ============================================

/**
 * 导出 Deal 数据
 */
export async function exportDeal(
  dealId: string,
  options: ExportRequest
): Promise<ExportResponse> {
  const response = await api.post<ExportResponse>(`/export/deal/${dealId}`, options)
  return response.data
}

/**
 * 下载导出文件
 */
export function downloadDealExport(
  dealId: string,
  exportType: ExportType = 'summary',
  format: ExportFormat = 'markdown'
): void {
  const baseUrl = api.defaults.baseURL || '/api/v1'
  const url = `${baseUrl}/export/deal/${dealId}/download?export_type=${exportType}&format=${format}`
  
  // 创建临时链接并触发下载
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', '')
  
  // 添加认证头（通过 cookie 或 header）
  // 注意：直接链接无法传递 Authorization header，需要后端支持 cookie 认证
  // 或者使用 fetch + blob 方式
  
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * 使用 Blob 方式下载（支持 Authorization header）
 */
export async function downloadDealExportWithAuth(
  dealId: string,
  exportType: ExportType = 'summary',
  format: ExportFormat = 'markdown'
): Promise<void> {
  const response = await api.get(`/export/deal/${dealId}/download`, {
    params: {
      export_type: exportType,
      format: format
    },
    responseType: 'blob'
  })
  
  // 从 Content-Disposition 获取文件名
  const contentDisposition = response.headers['content-disposition']
  let filename = `export_${dealId}.${format === 'markdown' ? 'md' : 'json'}`
  
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?([^;]+)/)
    if (filenameMatch) {
      filename = decodeURIComponent(filenameMatch[1].replace(/['"]/g, ''))
    }
  }
  
  // 创建 Blob 并触发下载
  const blob = new Blob([response.data], { type: response.headers['content-type'] })
  const url = window.URL.createObjectURL(blob)
  
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  
  // 清理
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export default {
  exportDeal,
  downloadDealExport,
  downloadDealExportWithAuth,
  exportTypeLabels,
  exportTypeDescriptions,
  exportFormatLabels
}

